import type { ExtractedData, ZohoVendor } from '../types';

/**
 * Suffixes juridiques à ignorer lors du matching (insensibles à la casse, avec
 * ou sans ponctuation). On les retire avant la comparaison normalisée.
 *
 * Couverture : France, UK, US, DE, NL, IT, ES, suisse, australie, asie courante.
 */
const LEGAL_SUFFIXES = [
  // FR
  'sarl', 'sa', 'sas', 'sasu', 'eurl', 'snc', 'sci', 'sca', 'scs', 'sel',
  'selarl', 'scop', 'scic', 'gie', 'ei', 'eirl', 'micro',
  // UK / IE
  'ltd', 'limited', 'plc', 'llp', 'lp',
  // US
  'inc', 'incorporated', 'corp', 'corporation', 'llc', 'lc', 'co',
  // DE / AT / CH
  'gmbh', 'ag', 'kg', 'ohg', 'eg', 'mbh',
  // NL / BE
  'bv', 'nv', 'cv', 'bvba', 'sprl', 'scrl',
  // IT
  'srl', 'spa', 'snc', 'sas', 'sapa',
  // ES / PT
  'sl', 'slu', 'sa', 'sad', 'lda', 'unipessoal',
  // SE / NO / DK / FI
  'ab', 'as', 'asa', 'aps', 'oy', 'oyj',
  // AU / NZ / Asie
  'pty', 'proprietary', 'pte', 'sdn', 'bhd',
  // génériques
  'group', 'holding', 'holdings', 'international',
] as const;

const LEGAL_SUFFIX_SET = new Set<string>(LEGAL_SUFFIXES);

/**
 * Normalise un nom pour matching :
 *   1. Décompose les diacritiques et retire les marques combinantes (NFD)
 *   2. Passe en minuscules
 *   3. Découpe en tokens alphanumériques
 *   4. Retire les tokens correspondant à un suffixe juridique
 *   5. Reconcatène sans séparateur
 *
 * Exemple : "Uploadcare Inc." → "uploadcare", "Atlassian Pty Ltd" → "atlassian",
 * "Le Victor Hugo" → "levictorhugo", "Qonto S.A.S" → "qonto".
 */
export function normalizeVendorName(s: string): string {
  if (!s) return '';
  const base = s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  // Tokens alphanum (espaces, points, virgules, apostrophes, tirets, slash, esperluettes…).
  const tokens = base.split(/[^a-z0-9]+/g).filter((t) => t.length > 0);
  const stripped = tokens.filter((t) => !LEGAL_SUFFIX_SET.has(t));
  // Si le retrait des suffixes vide la chaîne (ex: nom = "SA"), on garde tout
  // pour ne pas matcher tout le monde.
  return (stripped.length > 0 ? stripped : tokens).join('');
}

export interface VendorMatch {
  vendor: ZohoVendor;
  /** 'high' : noms identiques après normalisation (suffixes juridiques ignorés).
   *  'medium' : l'un est inclus dans l'autre, taille ≥ 4. */
  confidence: 'high' | 'medium';
}

/**
 * Cherche un vendor Zoho correspondant au supplier extrait par Claude.
 * Ordre :
 *   1. Égalité normalisée sur name OU company → high
 *   2. Inclusion bidirectionnelle (l'un contient l'autre), seuil 4 chars → medium
 *   3. null
 */
export function matchZohoVendor(
  extracted: Pick<ExtractedData['supplier'], 'name'> | null | undefined,
  vendors: ZohoVendor[],
): VendorMatch | null {
  if (!extracted?.name) return null;
  const target = normalizeVendorName(extracted.name);
  if (target.length < 2) return null;

  // 1) Égalité stricte sur la version normalisée.
  for (const v of vendors) {
    if (normalizeVendorName(v.name) === target) return { vendor: v, confidence: 'high' };
    if (v.company && normalizeVendorName(v.company) === target) {
      return { vendor: v, confidence: 'high' };
    }
  }

  // 2) Inclusion (garde-fou de longueur pour éviter "sa" qui matcherait tout).
  if (target.length >= 4) {
    for (const v of vendors) {
      const n = normalizeVendorName(v.name);
      if (n.length >= 4 && (n.includes(target) || target.includes(n))) {
        return { vendor: v, confidence: 'medium' };
      }
      if (v.company) {
        const c = normalizeVendorName(v.company);
        if (c.length >= 4 && (c.includes(target) || target.includes(c))) {
          return { vendor: v, confidence: 'medium' };
        }
      }
    }
  }

  return null;
}

/**
 * Recherche libre dans le select (combobox).
 * Préfère le préfixe → puis l'inclusion. Tri alphabétique ensuite.
 */
export function searchVendors(query: string, vendors: ZohoVendor[]): ZohoVendor[] {
  if (!query.trim()) return vendors;
  const q = normalizeVendorName(query);
  if (!q) return vendors;

  const matches: Array<{ v: ZohoVendor; score: number }> = [];
  for (const v of vendors) {
    const n = normalizeVendorName(v.name);
    const c = v.company ? normalizeVendorName(v.company) : '';
    let score = -1;
    if (n.startsWith(q) || c.startsWith(q)) score = 0;
    else if (n.includes(q) || c.includes(q)) score = 1;
    if (score >= 0) matches.push({ v, score });
  }
  matches.sort((a, b) => a.score - b.score || a.v.name.localeCompare(b.v.name));
  return matches.map((m) => m.v);
}
